import { useRef } from "react";
import clsx from "clsx";
import { Listbox } from "@headlessui/react";
import DropdownPanel from "../DropdownPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { enhancedChaseStatusData } from "./chaseListData";

type StatusOption = Record<string, any>;

interface IChaseItemStatusSelect {
  options: StatusOption[];
  value?: StatusOption;
  onChange: (option: StatusOption) => void;
  disabled?: boolean;
  loading?: boolean;
}

/** Status names are stored hyphenated — "Pending-Review" reads as "Pending review". */
const toLabel = (option: StatusOption) =>
  option?.label ?? String(option?.value ?? "").replace(/-/g, " ");

const getStyle = (option: StatusOption) =>
  enhancedChaseStatusData.find((status) => status.name === option?.value);

/** The shared status styles carry a border; a compact chip reads cleaner without one. */
const tintOnly = (bgColor?: string) =>
  (bgColor ?? "")
    .split(" ")
    .filter((token) => !token.includes("border"))
    .join(" ");

const ChaseItemStatusSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
}: IChaseItemStatusSelect) => {
  const selectedStyle = getStyle(value);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled || loading}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            ref={buttonRef}
            className={clsx(
              "h-8 w-full pl-2.5 pr-2.5 rounded-full flex items-center gap-1.5 text-[12px] font-semibold transition-all duration-200 outline-none",
              tintOnly(selectedStyle?.bgColor) || "bg-overlay-mid",
              selectedStyle?.borderColor ?? "border border-edge",
              selectedStyle?.color ?? "text-light-gray",
              disabled || loading
                ? "opacity-60 cursor-wait"
                : "cursor-pointer hover:brightness-[0.97] dark:hover:brightness-125"
            )}
          >
            {selectedStyle?.icon ? (
              <FontAwesomeIcon
                icon={selectedStyle.icon}
                className="text-[10px] shrink-0"
              />
            ) : null}

            <span className="truncate capitalize">
              {value ? toLabel(value) : "Select a status"}
            </span>

            <FontAwesomeIcon
              icon={loading ? faCircleNotch : faChevronDown}
              className={clsx(
                "ml-auto text-[9px] shrink-0 opacity-60",
                loading && "fa-spin"
              )}
            />
          </Listbox.Button>

          <DropdownPanel open={open} anchorRef={buttonRef} align="right" width={204}>
            <Listbox.Options
              static
              className="w-full max-h-[280px] overflow-y-auto custom-scrollbar p-1 rounded-xl bg-surface-elevated border border-edge shadow-overlay focus:outline-none"
            >
              {options.map((option) => {
                const style = getStyle(option);

                return (
                  <Listbox.Option
                    key={option?.value}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors duration-200",
                        active && "bg-overlay-hover"
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={clsx(
                            "h-6 w-6 rounded-md flex items-center justify-center shrink-0",
                            tintOnly(style?.bgColor) || "bg-overlay-mid",
                            style?.borderColor ?? "border border-edge",
                            style?.color ?? "text-light-gray"
                          )}
                        >
                          {style?.icon ? (
                            <FontAwesomeIcon
                              icon={style.icon}
                              className="text-[10px]"
                            />
                          ) : null}
                        </span>

                        <span className="text-[12px] font-medium text-text-primary capitalize truncate">
                          {toLabel(option)}
                        </span>

                        {selected ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="ml-auto text-[10px] text-accent shrink-0"
                          />
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </DropdownPanel>
        </div>
      )}
    </Listbox>
  );
};

export default ChaseItemStatusSelect;
